import { User } from '@livekatsomo/models';
import { LiveAvatar } from '@livekatsomo/web/ui-components/avatar';
import { NextLinkComposed } from '@livekatsomo/web/ui-components/link';
import Logout from '@mui/icons-material/Logout';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'next-i18next';
import { useState } from 'react';

export interface AccountMenuProps
  extends Pick<User, 'displayName' | 'photoURL' | 'email'> {
  onLogout?: () => void;
}

/**
 * A component that displays an account menu with options for account settings and logout.
 *
 * @param displayName The display name of the user.
 * @param photoURL The URL of the user's profile photo.
 * @param email The email address of the user.
 * @param onLogout A callback function to be called when the user clicks the logout button.
 * @returns A React component.
 */
export function AccountMenu({
  displayName,
  photoURL,
  email,
  onLogout,
}: AccountMenuProps) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const { t } = useTranslation();

  return (
    <>
      <Box
        data-test="account-menu"
        sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}
      >
        <Tooltip title={t('Account settings')}>
          <IconButton
            onClick={handleClick}
            size="small"
            // sx={{ ml: 2 }}
            aria-controls={open ? 'account-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
          >
            <LiveAvatar
              name={displayName || email || ''}
              url={photoURL ? photoURL : undefined}
            />
          </IconButton>
        </Tooltip>
      </Box>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        keepMounted
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem component={NextLinkComposed} to="/profile">
          <LiveAvatar
            name={displayName || email || ''}
            url={photoURL ? photoURL : undefined}
          />
          {displayName || t('My account')}
        </MenuItem>

        <Divider />
        <MenuItem onClick={onLogout}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          {t('Logout')}
        </MenuItem>
      </Menu>
    </>
  );
}

export default AccountMenu;
